import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Map, FileText, Bell, LogOut, Menu, X, ShieldCheck, MessageSquare, FileSpreadsheet, Compass, LifeBuoy } from 'lucide-react';
import { getClientToken, getClientInfo, clearClientSession, clientApi } from '../components/client-portal/clientPortalApi';
import ClientPortalLogin from './client-portal/ClientPortalLogin';
import ClientDashboard from './client-portal/ClientDashboard';
import ClientJourney from './client-portal/ClientJourney';
import ClientDocuments from './client-portal/ClientDocuments';
import ClientNotifications from './client-portal/ClientNotifications';
import ClientMessages from './client-portal/ClientMessages';
import ClientProposals from './client-portal/ClientProposals';
import ClientStrategy from './client-portal/ClientStrategy';
import ClientPostSettlement from './client-portal/ClientPostSettlement';

const NAV = [
  { key: 'dashboard', label: 'Home', icon: LayoutDashboard },
  { key: 'journey', label: 'Journey', icon: Map },
  { key: 'proposals', label: 'Proposals', icon: FileSpreadsheet },
  { key: 'strategy', label: 'Strategy', icon: Compass },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'messages', label: 'Messages', icon: MessageSquare },
  { key: 'after_settlement', label: 'After settlement', icon: LifeBuoy },
  { key: 'notifications', label: 'Updates', icon: Bell },
];

export default function ClientPortal() {
  const [authed, setAuthed] = useState(!!getClientToken());
  const [client, setClient] = useState(getClientInfo());
  const [view, setView] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!authed) return;
    let cancel = false;
    const tick = async () => {
      try {
        const items = await clientApi('/client/notifications');
        if (!cancel) setUnread(items.filter((n) => !n.read).length);
      } catch (_) {}
    };
    tick();
    const t = setInterval(tick, 30_000);
    return () => { cancel = true; clearInterval(t); };
  }, [authed, view]);

  const onLogin = (data) => {
    setClient(data.client);
    setAuthed(true);
    setView('dashboard');
  };

  const logout = () => {
    clearClientSession();
    setAuthed(false);
    setClient(null);
  };

  if (!authed) return <ClientPortalLogin onLoggedIn={onLogin} />;

  const NavItem = ({ item, mobile }) => {
    const Icon = item.icon;
    const active = view === item.key;
    return (
      <button
        type="button"
        data-testid={`nav-${item.key}`}
        onClick={() => { setView(item.key); setMobileOpen(false); }}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition w-full ${
          active ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'
        } ${mobile ? '' : 'justify-start'}`}
      >
        <Icon className="w-4 h-4" />
        <span>{item.label}</span>
        {item.key === 'notifications' && unread > 0 && (
          <span className="ml-auto bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full">{unread}</span>
        )}
      </button>
    );
  };

  const Page = () => {
    if (view === 'journey') return <ClientJourney />;
    if (view === 'proposals') return <ClientProposals />;
    if (view === 'strategy') return <ClientStrategy />;
    if (view === 'documents') return <ClientDocuments />;
    if (view === 'messages') return <ClientMessages />;
    if (view === 'after_settlement') return <ClientPostSettlement />;
    if (view === 'notifications') return <ClientNotifications />;
    return <ClientDashboard onNavigate={setView} />;
  };

  return (
    <div className="min-h-screen bg-page text-slate-900">
      {/* Top header */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
              aria-label="Toggle navigation"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            <div className="font-semibold tracking-tight">
              Derwent <span className="text-emerald-600">Finance</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right text-xs">
              <div className="font-medium text-slate-800 leading-tight">{client?.name}</div>
              <div className="text-slate-500 leading-tight">{client?.email}</div>
            </div>
            <button
              type="button"
              data-testid="client-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-rose-600 px-2 py-1 rounded-md"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Side nav (desktop) */}
        <aside className="hidden md:block">
          <nav className="space-y-1 sticky top-20" data-testid="client-portal-nav">
            {NAV.map((it) => <NavItem key={it.key} item={it} />)}
          </nav>
        </aside>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
            <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-200 p-3 space-y-1">
              {NAV.map((it) => <NavItem key={it.key} item={it} mobile />)}
            </div>
          </div>
        )}

        <main className="min-w-0">
          <Page />
        </main>
      </div>
    </div>
  );
}
